// ═══════════════════════════════════════════════════════════════════════════
//  chartTarget — "which SuperChart symbol/TF is THIS ledger row?" (God Phase 3,
//  2026-09-24). Kept apart from ContractChartDrawer so the dashboards can build
//  a target synchronously while the drawer itself stays a lazy chunk.
// ═══════════════════════════════════════════════════════════════════════════
import { ocOptionBase } from '../components/SuperChart/optionChainUtils';
import { optWithExpiry } from '../components/SuperChart/chartUtils';
import { canonTf, TF_TO_API } from '../components/SuperChart/chartConstants';
import { pvNormTf } from '../components/PineVersionPicker';
import { contractParts, type PnlRow } from './pnlMath';

export interface ChartTarget {
  /** SuperChart display symbol: "NIFTY 23250 CE" / "SILVERM FUT" / "RELIANCE". */
  symbol: string;
  /** Chart timeframe token ('1s'…'1D'); the row's own TF, canonicalised. */
  tf: string;
  botId?: string;
  /** Header text — the asset column's label ("NIFTY 50 · 23250 CE · 29-Sep"). */
  label: string;
  sub?: string;
  /** The same trade on its OWN contract (option premium / future) - the drawer's CONTRACT toggle. */
  alt?: ChartTarget;
  /** The traded option (God 2026-10-14): what the drawer needs to reprice the index's levels into premium. */
  opt?: { underlying: string; strike: number; cp: 'CE' | 'PE'; expiry: string };
  /** Epoch second to centre on (a trade's entry), and what to call it in the header. */
  focusTs?: number;
  focusLabel?: string;
}

/** Ledger TF ("1h", "1d", "5_Minute") → SuperChart TF ('1H', '1D', '5m').
 *  Falls back to '5m' when the row carries no usable timeframe. */
export function chartTfOf(tf?: string): string {
  const n = pvNormTf(tf) || String(tf || '').trim();
  const c = canonTf(n);
  return TF_TO_API[c] ? c : '5m';
}

/** The SuperChart symbol for a ledger row's OWN contract — built the way the
 *  SuperChart builds its bot panes (`${ocOptionBase(idx)} ${strike} CE`,
 *  `${base} FUT`), so history, feed key and pins all resolve identically. */
export function chartTargetForRow(row: PnlRow, botId?: string): ChartTarget {
  const p = contractParts(row);
  const base = ocOptionBase(p.underlyingKey);
  // THE TRADED CONTRACT, expiry included (God 2026-10-14 14:59: an expiry-less "SENSEX 73000 CE" pane drew another
  // expiry's premium and only seconds of 1s history - option files splice contracts at expiry)
  const symbol = p.kind === 'OPTION' && p.strike && p.optType
    ? (p.expiry ? optWithExpiry(`${base} ${p.strike} ${p.optType}`, p.expiry) : `${base} ${p.strike} ${p.optType}`)
    : p.kind === 'FUTURE' ? `${base} FUT` : p.underlyingKey;
  return {
    symbol, tf: chartTfOf(row.timeframe), botId: botId || (row.botId as string) || undefined,
    label: p.label,
    sub: [String(row.id || ''), row.timeframe ? `TF ${row.timeframe}` : ''].filter(Boolean).join(' · '),
    ...(p.kind === 'OPTION' && p.strike && (p.optType === 'CE' || p.optType === 'PE') && p.expiry
      ? { opt: { underlying: p.underlyingKey, strike: p.strike, cp: p.optType as 'CE' | 'PE', expiry: p.expiry } } : {}),
  };
}

/**
 * A chart for a (bot × symbol × TF) cell that has NO open contract to point at:
 * the instrument that lane actually trades — Shool lanes chart the futures
 * contract, option lanes chart the index the signal fires on, Sthambh the stock.
 */
export function chartTargetForCell(botId: string, symbol: string, tf: string): ChartTarget {
  const isShool = /shool/i.test(botId);
  const sym = isShool ? `${ocOptionBase(symbol)} FUT` : String(symbol || '').toUpperCase();
  return {
    symbol: sym, tf: chartTfOf(tf), botId,
    label: `${String(symbol || '').replace(/_/g, ' ')} · ${isShool ? 'FUT' : 'UNDERLYING'}`,
    sub: tf ? `TF ${tf}` : undefined,
  };
}

/** A ledger row's IST wall time ("HH:MM:SS" on dateIso) as an epoch second, or undefined. */
export function istEpoch(dateIso?: string, hms?: string): number | undefined {
  if (!dateIso || !hms || !/^\d{1,2}:\d{2}(:\d{2})?$/.test(String(hms).trim())) return undefined;
  const [h, m, sec = '00'] = String(hms).trim().split(':');
  const t = Date.parse(`${dateIso}T${h.padStart(2, '0')}:${m}:${sec}+05:30`);
  return Number.isFinite(t) ? Math.floor(t / 1000) : undefined;
}

/**
 * PINPOINT CHECK CHART (God 2026-09-29: "entry/exit time stamp ... chart par clean trade history"). The Pine speaks
 * on the UNDERLYING, and only the index pane draws its signals next to every fill of the cell, so a cell / trade
 * opens on the index at the cell's TF; the traded contract rides along as `alt`. With a row, the chart centres on
 * that trade's entry.
 */
export function chartTargetForPinpoint(botId: string, symbol: string, tf: string, row?: PnlRow): ChartTarget {
  const base = chartTargetForCell(botId, symbol, tf);
  if (!row) return base;
  const r: any = row;
  const focusTs = istEpoch(r.dateIso, r.time);
  return {
    ...base,
    alt: chartTargetForRow(row, botId),
    focusTs,
    focusLabel: `${String(r.id || '').replace(/^TRD-\d{8}-/, '')} · IN ${r.time || '?'} → OUT ${r.exitTime || 'open'}`,
  };
}
